import { ChangeDetectionStrategy, Component, HostListener } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { fadeInOut, menuInOut } from '@x/common/animations';
import { MenuNode, MenuService } from '@x/ecommerce-shop/app/core/menu/services/menu.service';
import { MenuSetActive } from '@x/ecommerce-shop/app/core/menu/state/menu.actions';
import { MenuState } from '@x/ecommerce-shop/app/core/menu/state/menu.state';
import { SearchUiStateService } from '@x/ecommerce-shop/app/core/search/services/search-ui-state.service';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'x-main-menu',
  templateUrl: './main-menu.component.html',
  styleUrls: ['./main-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [menuInOut, fadeInOut],
  host: { class: 'x-main-menu' },
})
export class MainMenuComponent {
  @Select(MenuState.mainMenu) nodes$: Observable<MenuNode[]>;
  @Select(MenuState.activeNodeAndAscendantIds) activeNodeIds$: Observable<number[]>;

  menuOperation$ = this.menuService.fetchMenuOperation$;

  private topLevelHoverNode$ = new BehaviorSubject<MenuNode | null>(null);
  private showSubMenu$ = new BehaviorSubject<boolean>(false);

  topLevelActiveNode$: Observable<number[]> = combineLatest([
    this.store.select(MenuState.activeNodeAndAscendantIds),
    this.topLevelHoverNode$,
  ]).pipe(
    map(([activeIds, hoveredNode]) => {
      if (hoveredNode) return [hoveredNode.id];

      return [...activeIds];
    }),
  );

  subMenu$: Observable<MenuNode | null> = combineLatest([
    this.topLevelHoverNode$,
    this.showSubMenu$,
  ]).pipe(
    switchMap(([menuNode, show]) => {
      if (show) return of(menuNode).pipe(delay(400));

      return of(null);
    }),
  );

  /*
   * The search lightbox and menu dropdown can potentially block each other
   * */
  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    const eventTarget = event.target as HTMLElement;
    const isSearchButton = Boolean(eventTarget?.parentElement?.classList.contains('search-button'));
    if (isSearchButton) return;

    setTimeout(() => {
      if (!this.topLevelHoverNode$.value) return;

      // Check if the currently hovered node is still the same
      this.searchUiStateService.closeAutoComplete();
      this.searchUiStateService.closeSearch();
    }, 400);
  }

  constructor(
    private store: Store,
    private searchUiStateService: SearchUiStateService,
    private menuService: MenuService,
  ) {}

  onTabEnter(node: MenuNode) {
    this.topLevelHoverNode$.next(node);
    this.showSubMenu$.next(false);
    this.showSubMenu$.next(true);
  }

  onMainMenuLeave() {
    this.topLevelHoverNode$.next(null);
  }

  setActive(node: MenuNode) {
    this.store.dispatch(new MenuSetActive(node));
    this.topLevelHoverNode$.next(null);
  }
}
